import { getMMBRoute, prependMarketToPath } from 'common';
import { t } from 'i18n';
import { DateTime } from 'luxon';
import { Location } from 'react-router-dom';
import {
  BoundFlight,
  Cabin,
  DateRange,
  dateTimeFrom,
  Environment,
  getCurrencyByPos,
  isInRange,
  PassengerCounts,
  PassengerErrorType,
  TripType,
  validatePassengerCounts,
} from 'shared';

import { appConfig, CepData, CepError } from './constants';
import { BookingState } from './store';

function getFormattedBoundDate(date: unknown, range: DateRange): string | undefined {
  const _date = typeof date === 'string' || date instanceof DateTime ? dateTimeFrom(date) : undefined;

  if (!_date?.isValid) return undefined;

  return isInRange({ range, date: _date }) ? _date.toFormat('yyyy-MM-dd') : undefined;
}

export function getFormattedBoundDates(
  outboundDate: unknown,
  inboundDate: unknown
): { outboundDate: string; inboundDate?: string } | undefined {
  const range = getBookingBoundary();

  const formattedOutboundDate = getFormattedBoundDate(outboundDate, range);

  if (!formattedOutboundDate) {
    return undefined;
  }

  if (!inboundDate) {
    return { outboundDate: formattedOutboundDate };
  }

  const formattedInboundDate = getFormattedBoundDate(inboundDate, range);

  if (!formattedInboundDate) {
    return undefined;
  }

  return formattedOutboundDate <= formattedInboundDate
    ? { outboundDate: formattedOutboundDate, inboundDate: formattedInboundDate }
    : undefined;
}

export function getCepDataFromQueryParams(location: Location): CepData {
  const currentUrlParams = new URLSearchParams(location.search);

  const origin = currentUrlParams.get('origin');
  const destination = currentUrlParams.get('destination');
  const outboundDateString = currentUrlParams.get('outboundDate') || currentUrlParams.get('outbounddate');
  const inboundDateString = currentUrlParams.get('inboundDate') || currentUrlParams.get('inbounddate');

  const adultsString = currentUrlParams.get('adults');
  const childrenString = currentUrlParams.get('children');
  const infantsString = currentUrlParams.get('infants');
  const youthsString = currentUrlParams.get('youths');

  const adults = adultsString ? parseInt(adultsString, 10) : 1;
  const children = childrenString ? parseInt(childrenString, 10) : 0;
  const infants = infantsString ? parseInt(infantsString, 10) : 0;
  const youths = youthsString ? parseInt(youthsString, 10) : 0;

  const { isValid: isPassengerCountValid } = validatePassengerCounts({ adults, children, infants, youths });

  let passengerCounts: PassengerCounts;
  if (isNaN(adults) || isNaN(children) || isNaN(infants) || !isPassengerCountValid) {
    passengerCounts = { adults: 1, children: 0, infants: 0, youths: 0 };
  } else {
    passengerCounts = { adults, children, infants, youths };
  }

  const boundDates = getFormattedBoundDates(outboundDateString, inboundDateString);

  const tripType = boundDates?.inboundDate ? TripType.ROUND_TRIP : TripType.ONE_WAY;

  return {
    origin: origin || undefined,
    destination: destination || undefined,
    tripType,
    passengerCounts,
    ...boundDates,
  };
}

type MixedValueObject = {
  [key: string]: string | number | DateTime | undefined | string[] | Record<string, unknown>;
};
export function generateKey(data: MixedValueObject) {
  const keys = Object.keys(data).sort();

  const key = keys
    .map(k => {
      const value = data[k];
      if (!value) return k;
      if (Array.isArray(value)) return `${k}-${value.join(',')}`;
      if (value instanceof DateTime) return `${k}-${value.toFormat('yyyy-MM-dd')}`;
      if (typeof value === 'object') return `${k}-${JSON.stringify(value)}`;
      return `${k}-${value.toString()}`;
    })
    .join('-');

  return `${key}-hash`;
}

export function getBookingBoundary() {
  const today = DateTime.local();
  const todayPlusOneYear = today.plus({ years: 1 }).endOf('month').startOf('day');
  const todayPlusFourDays = today.plus({ days: 4 }).startOf('day');

  return { from: todayPlusFourDays, to: todayPlusOneYear };
}

type Args = {
  tripType: TripType;
  outboundCabin?: Cabin;
  inboundCabin?: Cabin;
};

export const isBoundsSelected = ({ tripType, outboundCabin, inboundCabin }: Args) =>
  tripType !== TripType.ONE_WAY ? Boolean(outboundCabin) && Boolean(inboundCabin) : Boolean(outboundCabin);

type SarBookingArgs = {
  tripType: TripType;
  outboundFlight: BoundFlight;
  outboundDate: string;
  inboundDate?: string;
  passengerCounts: PassengerCounts;
};

export const getSarBookingUrl = ({
  tripType,
  outboundFlight,
  outboundDate,
  inboundDate,
  passengerCounts,
}: SarBookingArgs) => {
  const getTripType = () => `${tripType === TripType.ONE_WAY ? 'OW' : 'RT'}`;

  const getOnD = () => `${outboundFlight.origin.code}-${outboundFlight.destination.code}`;

  const getDates = () => {
    if (tripType === TripType.ONE_WAY) {
      return `${outboundDate.replaceAll('-', '')}`;
    }
    return `${outboundDate.replaceAll('-', '')}-${inboundDate?.replaceAll('-', '')}`;
  };

  const getPassengers = () => {
    const { adults, children, infants } = passengerCounts;
    return `a${adults}c${children}i${infants}y0`;
  };

  const domain = appConfig.ENVIRONMENT === Environment.Production ? location.hostname : 'test.sas.se';

  const pathname = prependMarketToPath('/book/flights');

  const search = `?search=${getTripType()}_${getOnD()}-${getDates()}_${getPassengers()}&view=upsell&bookingFlow=points`;

  return `https://${domain}${pathname}${search}`;
};

type MmbConfirmationUrlArgs = {
  pnr?: string;
  lastName?: string;
  orderId?: string;
};

export const getMmbConfirmationUrl = ({ pnr, lastName, orderId }: MmbConfirmationUrlArgs) => {
  const url = `${getMMBRoute(
    appConfig.ENVIRONMENT,
    appConfig.MMB_PATH
  )}/itinerary?bookingreference=${pnr}&names=${lastName}&orderId=${orderId}`;
  return url;
};

function addToDataLayer(data: GoogleAnalyticsTransactionPayload) {
  window.sasD360DataLayer = window.sasD360DataLayer || [];
  window.sasD360DataLayer.push(data);
}

type GoogleAnalyticsTransactionPayload = {
  event: 'purchase';
  origin: string;
  destination: string;
  payment_method: 'CreditCard';
  pos: string;
  channel: 'WEB';
  trip_type: TripType;
  outbound_cabin_class: string;
  inbound_cabin_class: string | undefined;
  price: number;
  tax: number;
  currency: string;
  points: number;
  discounted_points: number;
  voucher: string | undefined;
  booking_flow: 'SKY';
  adults: number;
  children: number;
  infants: number;
};

export const sendGoogleAnalyticsTransaction = (state: BookingState) => {
  const { origin, destination, pos, tripType, outboundCabin, inboundCabin, taxes } = state;
  if (!origin || !destination || !pos || !outboundCabin || !taxes) {
    return;
  }

  const outboundPrice = outboundCabin.price.type === 'points' ? outboundCabin.price : undefined;
  const inboundPrice = inboundCabin?.price.type === 'points' ? inboundCabin?.price : undefined;

  if (!outboundPrice) {
    return;
  }

  const totalPointsToPay = outboundPrice.discountedPoints + (inboundPrice?.discountedPoints ?? 0);
  const totalPoints = outboundPrice.totalPrice + (inboundPrice?.totalPrice ?? 0);
  const totalDiscountedPoints = totalPoints - totalPointsToPay;

  const data: GoogleAnalyticsTransactionPayload = {
    event: 'purchase',
    origin,
    destination,
    payment_method: 'CreditCard',
    pos,
    channel: 'WEB',
    trip_type: tripType,
    outbound_cabin_class: outboundCabin.cabin,
    inbound_cabin_class: inboundCabin?.cabin,
    price: taxes.totalTax,
    tax: taxes.totalTax,
    currency: getCurrencyByPos(pos).CURRENCY_CODE,
    points: totalPointsToPay,
    discounted_points: totalDiscountedPoints,
    voucher: state.selectedCoupons[0]?.code,
    booking_flow: 'SKY',
    adults: state.passengerCounts.adults,
    children: state.passengerCounts.children,
    infants: state.passengerCounts.infants,
  };
  addToDataLayer(data);
};

const cepErrorMessages = {
  [CepError.FIELD_REQUIRED]: t('common:form.fieldRequired'),
  [PassengerErrorType.INVALID_PAX]: 'Invalid passenger count',
  [PassengerErrorType.INVALID_CEPID]: 'Invalid cep id',
  [PassengerErrorType.AT_LEAST_ONE_PAX]: 'Invalid passenger count',
  [PassengerErrorType.MAX_NINE_ADULTS_AND_CHILDREN]: 'Invalid passenger count',
  [PassengerErrorType.INFANT_EXCEEDS_ADULT]: 'Invalid passenger count',
  [PassengerErrorType.YOUNG_COUNT_ERROR]: 'Invalid passenger count',
  [PassengerErrorType.AT_LEAST_ONE_YOUNG]: 'Invalid passenger count',
  [PassengerErrorType.MAX_NINE_YOUNG]: 'Invalid passenger count',
  [PassengerErrorType.MAX_NINE_CHILD]: 'Invalid passenger count',
  [PassengerErrorType.UMNR_COUNT_ERROR]: 'Invalid passenger count',
};

export const getCepErrorMessage = (errorType: CepError | PassengerErrorType) => {
  return cepErrorMessages[errorType] ?? errorType;
};

// Added for legact login token support
function getAuthCookie() {
  const cookieValue = document.cookie
    .split('; ')
    .find(row => row.startsWith('LOGIN_AUTH='))
    ?.split('=')[1];

  if (!cookieValue) {
    console.warn('Legacy login auth cookie not found');
  }

  return cookieValue;
}

export function getAuthorization() {
  return `Bearer ${decodeURIComponent(getAuthCookie() ?? '')}`;
}
